import React from 'react';

interface AddStudentFormSkeletonProps {
  /**
   * Number of form fields to display in the skeleton
   */
  fieldCount?: number;

  /**
   * Additional class names to apply to the skeleton container
   */
  className?: string;
}

/**
 * AddStudentFormSkeleton component
 *
 * This component provides a loading skeleton specifically designed for the Add Student form.
 * It shows loading states for the form steps sidebar and form fields.
 *
 * @example
 * <AddStudentFormSkeleton />
 */
export const AddStudentFormSkeleton: React.FC<AddStudentFormSkeletonProps> = ({
  fieldCount = 8,
  className = '',
}) => {
  // Generate an array of dummy items for steps
  const steps = Array(8).fill(null);

  return (
    <div className={`min-h-screen bg-[#F5F8F9] ${className}`}>
      <div className="max-w-[1280px] mx-auto px-2 py-4">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="w-48 h-8 bg-gray-200 rounded-md animate-pulse"></div>
            <div className="w-32 h-5 bg-gray-200 rounded-md animate-pulse mt-2"></div>
          </div>
          <div className="w-20 h-10 bg-gray-200 rounded-md animate-pulse"></div>
        </div>

        {/* Steps Progress */}
        <div className="mb-6 overflow-hidden rounded-xl border border-[#E4E8EA] bg-white p-4">
          <div className="flex items-center space-x-1">
            {steps.map((_, index) => (
              <React.Fragment key={index}>
                <div
                  className={`
                    h-12 w-28 rounded-lg bg-gray-200 animate-pulse
                    ${index === 0 ? 'bg-gray-300' : 'bg-gray-200'}
                  `}
                  style={{ animationDelay: `${index * 0.1}s` }}
                ></div>
                {index < steps.length - 1 && (
                  <div className="h-1 w-4 bg-gray-200 animate-pulse"></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="mt-4 lg:mb-20 mb-36">
          <div className="bg-white rounded-xl shadow-sm border border-[#E4E8EA] p-4">
            {/* Form Title */}
            <div className="w-64 h-8 bg-gray-200 rounded-md animate-pulse mb-6"></div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array(fieldCount)
                .fill(null)
                .map((_, index) => (
                  <div
                    key={index}
                    className="space-y-2"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="w-32 h-5 bg-gray-200 rounded-md animate-pulse"></div>
                    <div className="w-full h-12 bg-gray-200 rounded-md animate-pulse"></div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E4E8EA] py-3 px-4 lg:px-6">
        <div className="max-w-[1280px] mx-auto flex justify-between">
          <div className="w-24 h-10 bg-gray-200 rounded-md animate-pulse"></div>
          <div className="w-24 h-10 bg-gray-300 rounded-md animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};
